import { Roles } from '../Constants/BorgEnums';
import { fetchFromHttp, postToHttp, patchToHttp, deleteFromHttp, putToHttp, downloadFromHttp } from './AxiosService';

export const getAllUsers = async (orgId?: string) => {
  let result;

  //If an orgName is passed send query parameter, otherwise do not pass the parameter
  if (orgId)
    result = await fetchFromHttp(`/users?orgId=${orgId}`);
  else
    result = await fetchFromHttp(`/users`);

  return result;
};

export const getAllUsersDetailed = async (orgId?: string) => {
  let result;
  if (orgId)
    result = await fetchFromHttp(`/users/admin-detailed?orgId=${orgId}`);
  else
    result = await fetchFromHttp(`/users/admin-detailed`);
  return result;
};

export const getAllUsersByOrgName = async (orgName?: string) => {
  let result;

  //If an orgName is passed send query parameter, otherwise do not pass the parameter
  if (orgName)
    result = await fetchFromHttp(`/users?orgName=${orgName}`);
  else
    result = await fetchFromHttp(`/users`);

  return result;
};

export const getUserStats = async (orgId?: string) => {
  let userStats;

  //If an orgId is passed send query parameter, otherwise do not pass the parameter
  if (orgId)
    userStats = await fetchFromHttp(`/users/stats?orgId=${orgId}`);
  else
    userStats = await fetchFromHttp('/users/stats');

  return userStats;
};

export const getAccessCodes = async () => {
  let result = await fetchFromHttp('/users/accessCodes');
  return result;
}

export const generateAccessCode = async () => {
  let result = await postToHttp('/users/accessCodes');
  return result;
}

export const deleteAccessCode = async (accessCodeId: string) => {
  let res = await deleteFromHttp(`/users/accessCodes/${accessCodeId}`)
  return res;
}

export const createOrganization = async (orgName: string) => {
  let result = await postToHttp(`/organizations/${orgName}`);
  return result;
}

export const getOrganizationByName = async (orgName: string) => {
  let res = await fetchFromHttp(`/organizations/names/${orgName}`);
  return res;
};

export const getOrganization = async (orgId: string) => {
  let res = await fetchFromHttp(`/organizations/${orgId}`);
  return res;
};

export const getAllOrganizations = async () => {
  let res = await fetchFromHttp(`/organizations`);
  return res;
};

export const deleteOrganization = async (orgName: string) => {
  let res = await deleteFromHttp(`/organizations/${orgName}`)
  return res;
}

export const updateOrganizationPaidStatus = async (orgId: string, isPaid: boolean) => {
  let res = await patchToHttp(`/organizations/${orgId}/status?isPaid=${isPaid}`);
  return res;
};

export const updateOrganizationUserLimit = async (orgId: string, userLimit: number) => {
  let res = await patchToHttp(`/organizations/${orgId}/userlimit?userLimit=${userLimit}`);
  return res;
};

export const assignOrganizationToAccessCode = async (accessCode: string, organizationId: string) => {
  await patchToHttp(`/organizations/accessCodes/${accessCode}/${organizationId}`);
};

export type StartTrialPayload = {
  startTrial: true;
  durationMonths?: number;
  trialEndDate?: string;
};

export type EditTrialPayload = {
  endNow?: boolean;
  endNowAndSetPaid?: boolean;
  extendMonths?: number;
  trialEndDate?: string;
};

export const updateOrganizationTrial = async (orgId: string, payload: StartTrialPayload | EditTrialPayload) => {
  const res = await patchToHttp(`/organizations/${orgId}/trial`, payload);
  return res;
};

export const loginUser = async (user: any) => {
  let res = await postToHttp('/auth/session', user);
  localStorage.setItem('refresh-token', res.refreshToken);
  let userIdStr = JSON.stringify(res.id);
  localStorage.setItem('userId', userIdStr);
  localStorage.setItem('canPlay', JSON.stringify(res.canPlay));
  return userIdStr;
}

export const logoutUser = async (refreshToken: any) => {
  await deleteFromHttp('/auth/session', refreshToken);
}

//Check if user has role permissions to access resource
export const checkRole = async (roles: Roles[]) => {

  let refreshToken = localStorage.getItem('refresh-token');
  if (!refreshToken) return false

  let res = await fetchFromHttp(`/auth/session/roles`);
  let userRoles = res.data;
  let hasRole = false;

  roles.forEach(role => {
    if (userRoles.includes(role)) {
      hasRole = true;
    }
  });

  return hasRole;
}

export const getUser = async (userId: string) => {
  let res = await fetchFromHttp(`/users/${userId}`);
  return res;
};

export const updateUser = async (user: any) => {
  await putToHttp(`/users/${user.id}`, user);
};

export const updateUserLastGroupSelectionId = async (userId: string, groupId: string) => {
  let res = await patchToHttp(`/users/${userId}/groupSelection/latest/${groupId}`);
  return res;
};

export const toggleUserEnabled = async (userId: string) => {
  await patchToHttp(`/users/${userId}/enabled`);
};

export const deleteUser = async (userId: string) => {
  let res = await deleteFromHttp(`/users/${userId}`)
  return res;
}

export const updateUserRole = async (userId: string, newRole: Roles) => {
  let res = await patchToHttp(`/users/${userId}/role/${newRole}`);
  return res;
};

export const forceUserPasswordReset = async (userId: string) => {
  let res = await postToHttp(`/users/passwordResets/${userId}`)
  return res;
}

export const getUserPasswordResetPending = async (userName: string) => {
  let res = await fetchFromHttp(`/users/passwordResets/${userName}`)
  return res.data;
}

export const resetUserPassword = async (request: any) => {
  let res = await patchToHttp(`/users/passwordResets/${request.username}`, request)
  return res;
}

export const requestPasswordReset = async (email: string) => {
  let res = await postToHttp(`/users/requestPasswordReset`, email)
  return res;
}

export const resetPassword = async (dto: { email: string; token: string; newPassword: string }) => {
  let res = await postToHttp('/users/resetPassword', dto);
  return res;
}

export const checkPasswordResetAvailable = async () => {
  let res = await fetchFromHttp('/users/passwordResetAvailable');
  return res.data;
};

export const getAllSandboxes = async () => {
  let result = await fetchFromHttp('/sandboxes');
  return result;
};

export const getAllSandboxesExtended = async () => {
  let result = await fetchFromHttp(`/sandboxes/extended`);
  return result;
};

export const toggleSandboxShared = async (sandboxId: string) => {
  await patchToHttp(`/sandboxes/${sandboxId}/shared`);
};

export const deleteSandbox = async (sandboxId: string) => {
  let res = await deleteFromHttp(`/sandboxes/${sandboxId}`)
  return res;
}

export const downloadSandboxToJson = async (sandboxId: string, filename: string) => {
  let res = await downloadFromHttp(`/sandboxes/${sandboxId}/json`, filename, "application/json" )
  return res;
}

export const getSessionLeaderboard = async (
  sessionId: string, courseId: string, campaignId?: string,
  scenarioId?: string, userId?: string, limit?: number
) => {
  let path = `/scores/leaderboard/${sessionId}/${courseId}`;

  // Create an array to hold query parameters
  let queryParams: string[] = [];

  // Add parameters to the array if they are defined
  if (campaignId) queryParams.push(`campaignId=${campaignId}`);
  if (scenarioId) queryParams.push(`scenarioId=${scenarioId}`);
  if (userId) queryParams.push(`userId=${userId}`);
  if (limit) queryParams.push(`limit=${limit}`);

  // If there are query parameters, append them to the path
  if (queryParams.length > 0) {
    path += '?' + queryParams.join('&');
  }

  let result = await fetchFromHttp(path);

  return result;
};

export const getUserScoreCards = async (
  userId: string, sessionId: string, courseId: string, 
  campaignId?: string, scenarioId?: string, includeFailedScores?: boolean
) => {
  let path = `/scores/${userId}/${sessionId}/${courseId}`;

  // Create an array to hold query parameters
  let queryParams: string[] = [];

  // Add parameters to the array if they are defined
  if (campaignId) queryParams.push(`campaignId=${campaignId}`);
  if (scenarioId) queryParams.push(`scenarioId=${scenarioId}`);
  if (includeFailedScores) queryParams.push(`includeFailedScores=${includeFailedScores}`);

  // If there are query parameters, append them to the path
  if (queryParams.length > 0) {
    path += '?' + queryParams.join('&');
  }

  let result = await fetchFromHttp(path);

  return result;
};

export const getSessionScoreCards = async (
  sessionId: string, courseId: string, campaignId?: string,
  scenarioId?: string, userId?: string, includeFailedScores?: boolean
) => {
  let path = `/scores/session/${sessionId}/${courseId}`;

  // Create an array to hold query parameters
  let queryParams: string[] = [];

  // Add parameters to the array if they are defined
  if (campaignId) queryParams.push(`campaignId=${campaignId}`);
  if (scenarioId) queryParams.push(`scenarioId=${scenarioId}`);
  if (userId) queryParams.push(`userId=${userId}`);
  if (includeFailedScores) queryParams.push(`includeFailedScores=${includeFailedScores}`);

  // If there are query parameters, append them to the path
  if (queryParams.length > 0) {
    path += '?' + queryParams.join('&');
  }

  let result = await fetchFromHttp(path);

  return result;
};

export const removeAllScoreCards = async () => {
  let res = await deleteFromHttp('/scores');
  return res;
}

export const downloadScenarioResultsToCSV = async (sessionId: string, scenarioId: string, filename: string) => {
  let res = await downloadFromHttp(`/scores/session/${sessionId}/${scenarioId}/csv`, filename, "text/csv" );
  return res;
}

export const downloadGradebookToCSV = async (sessionId: string, courseId: string, filename: string) => {
  let res = await downloadFromHttp(`/scores/session/${sessionId}/${courseId}/gradebook/csv`, filename, "text/csv" );
  return res;
}

export const stripePayandRegisterUser = async (userData: any, paymentMethodId: any) => {
  const res = await postToHttp('/payments/register-and-pay', {
    userRegistrationDetails: userData,
    paymentMethodId: paymentMethodId,
  });
  return res;
};

export const validateUserRegistration = async (userData: any) => {
  const res = await postToHttp('/users/validate-registration', userData);
  return res;
};

export const registerUserWithOrganization = async (userData: any) => {
  const res = await postToHttp(`/users/${userData.organization}`, userData);
  return res;
};

export const registerUserWithCode = async (userData: any) => {
  const res = await postToHttp(`/users/registrationCode`, userData);
  return res;
};

export const registerPaymentFreeUser = async (userData: any) => {
  const res = await postToHttp(`/users/paymentFreeRegistration`, userData);
  return res;
};

export const addGroup = async (groupData: any) => {
  let res = await postToHttp('/groups', groupData);
  return res;
};

export const getGroups = async (ownerUserId: string = "") => {
  let res = await fetchFromHttp(`/groups?ownerUserId=${ownerUserId}`);
  return res;
};

export const getGroupsPerOrganization = async (ownerOrganizationId: string) => {
  let res = await fetchFromHttp(`/groups?ownerOrganizationId=${ownerOrganizationId}`);
  return res;
};

export const getGroup = async (groupId: string) => {
  let res = await fetchFromHttp(`/groups/${groupId}`);
  return res;
};

export const UpdateGroupOwners = async (groupId: string, updatedOwnerIds: string[] ) => {
  const request = { ownerIds: updatedOwnerIds };
  let res = await patchToHttp(`/groups/${groupId}/owners`, request);
  return res;
};

export const removeGroup = async (groupId: string) => {
  let res = await deleteFromHttp(`/groups/${groupId}`);
  return res;
}

export const startSession = async (groupId: string) => {
  let res = await patchToHttp(`/groups/${groupId}/activesession`);
  return res;
};

export const scheduleSession = async (groupId: string, startDate: string) => {
  const request = { scheduledStartDate: startDate };
  let res = await patchToHttp(`/groups/${groupId}/scheduledsession`, request);
  return res;
};

export const getSession = async (groupId: string) => {
  let res = await fetchFromHttp(`/groups/${groupId}/activesession`);
  return res;
};

export const getScheduledSession = async (groupId: string) => {
  const res = await fetchFromHttp(`/groups/${groupId}/scheduledsession`);
  return res;
};

export const getCompetition = async (competitionId: string) => {
  let res = await fetchFromHttp(`/groups/competitions/${competitionId}`);
  return res;
};

export const getSessionWithSessionId = async (sessionId: string) => {
  let res = await fetchFromHttp(`/groups/competitions/${sessionId}`);
  return res;
};

export const stopSession = async (groupId: string, disableMemberAccounts?: boolean) => {
  let res;
  //If disableMemberAccounts is passed send query parameter, otherwise do not pass the parameter
  if (disableMemberAccounts)
    res = await deleteFromHttp(`/groups/${groupId}/activesession?disableMemberAccounts=${disableMemberAccounts}`);
  else
    res = await deleteFromHttp(`/groups/${groupId}/activesession`);

  return res;
};

export const resumeSession = async (groupId: string, reenableMemberAccounts?: boolean) => {
  let res;
  //If reenableMemberAccounts is passed send query parameter, otherwise do not pass the parameter
  if (reenableMemberAccounts)
    res = await patchToHttp(`/groups/${groupId}/lastactivesession?reenableMemberAccounts=${reenableMemberAccounts}`);
  else
    res = await patchToHttp(`/groups/${groupId}/lastactivesession`);

  return res;
};

export const updateSessionEndDate = async (groupId: string, newEndDate: string, isImmediate: boolean, disableUsersOnEnd: boolean = false) => {
  const payload = { newEndDate, isImmediate, disableUsersOnEnd };
  let res = await patchToHttp(`/groups/${groupId}/enddate`, payload);
  return res;
};

export const getScenarioHeaders = async (courseId: string) => {
  let res = await fetchFromHttp(`/courses/${courseId}/headers`);
  return res;
};

export const setBannerMessage = async (bannerMessage: any) => {
  await postToHttp(`/systemSettings/bannerMessage`, bannerMessage);
};

export const getBannerMessage = async () => {
  let res = await fetchFromHttp(`/systemSettings/bannerMessage`);
  return res;
};

export const cleanupDatabaseAPI = async () => {
  let res = await deleteFromHttp(`/test/cleanupDatabase`)
  return res;
}